import { useState, useEffect } from 'react';
import { HeartIcon, MessageSquareIcon, EyeIcon, BarChart3Icon, FlameIcon, SparklesIcon, Share2Icon, XIcon } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import type { Snap, SnapActivity } from '@/types/database.types';
import CategoryTags from '@/components/CategoryTags';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';

interface SnapFeedCardProps {
  snap: Snap;
  onVote: (optionId: string) => void;
  onRemoveVote?: () => void;
  onLike: (event: React.MouseEvent) => void;
  onShare: () => void;
}

export default function SnapFeedCard({ snap, onVote, onRemoveVote, onLike, onShare }: SnapFeedCardProps) {
  const [imageError, setImageError] = useState<Record<string, boolean>>({});
  const [isVoting, setIsVoting] = useState(false);
  const [prevVoteCounts, setPrevVoteCounts] = useState<Record<string, number>>({});

  // Store previous vote counts for animation
  useEffect(() => {
    const newCounts: Record<string, number> = {};
    snap.options?.forEach(option => {
      newCounts[option.id] = option.vote_count;
    });
    setPrevVoteCounts(newCounts);
  }, [snap.options]);

  const handleImageError = (optionId: string) => {
    setImageError(prev => ({
      ...prev,
      [optionId]: true
    }));
  };

  // Handle option click to vote
  const handleOptionClick = async (optionId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event bubbling to parent
    
    // Prevent multiple votes while processing
    if (isVoting) return;
    
    // If already voted for this option, do nothing
    if (snap.userVote === optionId) {
      toast.error('You have already voted for this option');
      return;
    }
    
    try {
      setIsVoting(true);
      await onVote(optionId);
    } finally {
      // Ensure isVoting is reset even if there's an error
      setTimeout(() => setIsVoting(false), 500);
    }
  };

  const handleRevokeVote = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event bubbling to parent
    
    if (!onRemoveVote || isVoting || !snap.userVote) return;
    
    try {
      setIsVoting(true);
      await onRemoveVote();
    } finally {
      setTimeout(() => setIsVoting(false), 500);
    }
  };

  // Function to render activity badge
  const renderActivityBadge = (activity: SnapActivity | undefined) => {
    if (!activity) return null;
    
    switch (activity) {
      case 'hot':
        return (
          <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <div className="flex -space-x-1">
              <FlameIcon className="h-3.5 w-3.5" />
              <FlameIcon className="h-3.5 w-3.5" />
              <FlameIcon className="h-3.5 w-3.5" />
            </div>
            <span className="ml-1">Hot</span>
          </div>
        );
      case 'trending':
        return (
          <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
            <FlameIcon className="h-3.5 w-3.5 mr-1" />
            <span className="ml-1">Trending</span>
          </div>
        );
      case 'new':
        return (
          <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <SparklesIcon className="h-3.5 w-3.5 mr-1" />
            <span className="ml-1">New</span>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 bg-gradient-to-r from-primary-600 to-primary-700 text-white">
        {/* Activity Badge */}
        <div className="mb-2">
          {snap.activity && renderActivityBadge(snap.activity)}
          {snap.is_system && (
            <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 ml-2">
              <FlameIcon className="h-3.5 w-3.5 mr-1" />
              FIRESNAP
            </div>
          )}
        </div>
        
        <h2 className="text-xl font-bold">{snap.title}</h2>
        
        {snap.description && (
          <p className="mt-2 text-sm text-white/90">{snap.description}</p>
        )}
        
        <div className="mt-3">
          <CategoryTags categories={snap.categories} variant="white" />
        </div>
        
        <div className="mt-3 flex items-center text-sm text-white/80">
          <span>
            {snap.is_anonymous ? 'Anonymous' : snap.creator?.username || 'Unknown'}
          </span>
          <span className="mx-2">•</span>
          <span>
            {formatDistanceToNow(new Date(snap.ends_at), { addSuffix: true })}
          </span>
        </div>
      </div>
      
      {/* Options */}
      <div className="flex-grow overflow-y-auto p-4">
        <div className="space-y-4">
          {snap.options?.map((option) => {
            const percentage = snap.total_votes > 0
              ? (option.vote_count / snap.total_votes) * 100
              : 0;
            
            const isVoted = snap.userVote === option.id;
            
            return (
              <motion.div 
                key={option.id}
                className={`bg-gray-50 p-4 rounded-lg border transition-colors ${
                  isVoted 
                    ? 'border-primary-500 bg-primary-50' 
                    : 'border-gray-200 hover:border-primary-300'
                }`}
                onClick={(e) => handleOptionClick(option.id, e)}
                whileHover={{ scale: 1.01 }}
                initial={{ opacity: 0.9 }}
                animate={{ opacity: 1 }}
              >
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-medium">{option.title}</h3>
                  <div className="flex items-center">
                    {isVoted && (
                      <span className="bg-primary-500 text-white text-xs px-1.5 py-0.5 rounded-full mr-1.5 whitespace-nowrap">
                        <span className="hidden xs:inline">Your </span>Vote
                      </span>
                    )}
                    <AnimatePresence mode="wait">
                      <motion.span 
                        key={`percent-${percentage.toFixed(1)}`}
                        initial={{ opacity: 0, x: -5 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 5 }}
                        transition={{ duration: 0.3 }}
                        className="text-sm font-semibold"
                      >
                        {percentage.toFixed(1)}%
                      </motion.span>
                    </AnimatePresence>
                  </div>
                </div>
                
                {option.description && (
                  <p className="text-sm text-gray-600 mb-2">{option.description}</p>
                )}
                
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <motion.div
                    className={`h-full ${isVoted ? 'bg-primary-500' : 'bg-primary-600'}`}
                    initial={{ width: `${prevVoteCounts[option.id] ? (prevVoteCounts[option.id] / (snap.total_votes || 1)) * 100 : 0}%` }}
                    animate={{ width: `${percentage}%` }}
                    transition={{ 
                      duration: 0.5,
                      ease: "easeInOut"
                    }}
                    key={`${option.id}-${option.vote_count}-${snap.total_votes}`} // Key changes when vote counts change
                  />
                </div>
                
                {isVoted && onRemoveVote && (
                  <div className="mt-2 flex justify-end">
                    <button
                      onClick={handleRevokeVote}
                      disabled={isVoting}
                      className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-600 bg-red-50 rounded hover:bg-red-100 transition-colors"
                    >
                      <XIcon className="h-3 w-3 mr-1" />
                      Revoke vote
                    </button>
                  </div>
                )}
                
                {option.image_url && !imageError[option.id] && (
                  <div className="mt-3">
                    <img
                      src={option.image_url}
                      alt={option.title}
                      className="w-full h-32 object-cover rounded-lg"
                      onError={() => handleImageError(option.id)}
                    />
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
      
      {/* Footer */}
      <div className="p-4 border-t border-gray-200 bg-white">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-4 text-gray-600">
            <div className="flex items-center" title="Total Votes">
              <BarChart3Icon className="h-5 w-5 mr-1" />
              <AnimatePresence mode="wait">
                <motion.span
                  key={snap.total_votes}
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 5 }}
                  transition={{ duration: 0.3 }}
                >
                  {snap.total_votes}
                </motion.span>
              </AnimatePresence>
            </div>
            <div className="flex items-center" title="Views">
              <EyeIcon className="h-5 w-5 mr-1" />
              <span>{snap.view_count}</span>
            </div>
            <div className="flex items-center" title="Comments">
              <MessageSquareIcon className="h-5 w-5 mr-1" />
              <span>{snap.commentCount}</span>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            <button
              onClick={onLike}
              className={`flex items-center transition-colors ${
                snap.userLiked ? 'text-red-500 hover:text-red-600' : 'text-gray-600 hover:text-red-500'
              }`}
              title={snap.userLiked ? 'Unlike' : 'Like'}
              aria-label={snap.userLiked ? 'Unlike' : 'Like'}
            >
              <HeartIcon className={`h-5 w-5 mr-1 ${snap.userLiked ? 'fill-current' : ''}`} />
              <span className="leading-none">{snap.likes_count}</span>
            </button>
            
            <button
              onClick={(e) => {
                e.stopPropagation();
                onShare();
              }}
              className="flex items-center text-gray-600 hover:text-primary-600 transition-colors"
              title="Share SNAP"
              aria-label="Share SNAP"
            >
              <Share2Icon className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}